
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { SignInForm } from '@/components/auth/SignInForm';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { getLogger } from '@/utils/logger';
import { toast } from 'sonner';

const log = getLogger({ module: 'AuthPage' });

const Auth = () => {
  const navigate = useNavigate();
  const { toast: toastHook } = useToast();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<'sign_in' | 'sign_up' | 'reset_password'>('sign_in');

  // Check URL params for view type
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'reset-password') {
      setView('reset_password');
    } else if (viewParam === 'sign_up') {
      setView('sign_up');
    } else {
      setView('sign_in');
    }
  }, [searchParams]);

  // If user is already authenticated, redirect to home (UNLESS they're resetting password)
  useEffect(() => {
    // Check searchParams directly to avoid race condition with view state
    const viewParam = searchParams.get('view');
    if (user && viewParam !== 'reset-password' && view !== 'reset_password') {
      log.info('Auth page - User already authenticated, redirecting to home');
      navigate('/', { replace: true });
    }
  }, [user, navigate, view, searchParams]);

  const renderContent = () => {
    switch (view) {
      case 'reset_password':
        return <PasswordResetForm />;
      case 'sign_up':
        return <SignUpForm />;
      default:
        return <SignInForm />;
    }
  };

  const getTitle = () => {
    switch (view) {
      case 'reset_password':
        return 'Reset Your Password';
      case 'sign_up':
        return 'Create Account';
      default:
        return 'Welcome to FOMO';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-background/80 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-black/20 backdrop-blur-sm p-8 rounded-lg border border-white/10">
        <h1 className="text-2xl font-bold text-center mb-8 text-white">{getTitle()}</h1>
        
        {view !== 'reset_password' && (
          <div className="flex justify-center space-x-4 mb-8">
            <button
              onClick={() => setView('sign_in')}
              className={`px-4 py-2 rounded-md transition-colors ${
                view === 'sign_in' 
                  ? 'bg-primary text-white' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setView('sign_up')}
              className={`px-4 py-2 rounded-md transition-colors ${
                view === 'sign_up' 
                  ? 'bg-primary text-white' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Sign Up
            </button>
          </div>
        )}
        
        {renderContent()}
        
        {view === 'reset_password' && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setView('sign_in')}
              className="text-primary hover:text-primary/80 text-sm"
            >
              Back to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Password Reset Form Component
const PasswordResetForm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password updated successfully!');
        navigate('/', { replace: true });
      }
    } catch (error) {
      toast.error('Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="password" className="block text-white text-sm font-medium mb-2">
          New Password
        </label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-md text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter your new password"
        />
      </div>
      
      <div>
        <label htmlFor="confirmPassword" className="block text-white text-sm font-medium mb-2">
          Confirm Password
        </label>
        <input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-md text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Confirm your new password"
        />
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? 'Updating...' : 'Update Password'}
      </button>
    </form>
  );
};

export default Auth;
